import { inject, Injectable } from '@angular/core';
import { Observable, catchError, map, of } from 'rxjs';
import { DevPortalHttpClient } from './dev-portal-http-client.service';
import { GroupResponse } from '../models/graph/group-response.model';
import { UserResponse } from '../models/graph/user-response.model';

@Injectable({ providedIn: 'root' })
export class GraphSearchService {
  private readonly httpClient = inject(DevPortalHttpClient);

  searchUsers(query: string): Observable<UserResponse[]> {
    const term = String(query ?? '').trim();
    if (!term) return of([]);

    // NOTE: DevPortalHttpClient baseUrl already includes `/api`.
    // So we call `/graph/...` not `/api/graph/...`.
    return this.httpClient.Get<unknown>('/graph/search-users', { query: term }).pipe(
      map((res) => normalizeGraphSearchResponse(res)),
      catchError(() => of([])),
    );
  }

  searchGroups(query: string): Observable<GroupResponse[]> {
    const term = String(query ?? '').trim();
    if (!term) return of([]);

    return this.httpClient.Get<unknown>('/graph/search-groups', { query: term }).pipe(
      map((res) => normalizeGraphSearchResponse(res)),
      catchError(() => of([])),
    );
  }
}

function normalizeGraphSearchResponse(res: unknown): Array<{ displayName: string; email: string; id: string }> {
  const raw = extractSuggestionArray(res);

  const mapped = raw
    .map((item) => {
      if (!item || typeof item !== 'object') return null;
      const o = item as Record<string, unknown>;

      // Support both camelCase and PascalCase just in case.
      const displayName = String(o['displayName'] ?? o['DisplayName'] ?? '').trim();
      const email = String(o['email'] ?? o['Email'] ?? '').trim();
      const id = String(o['id'] ?? o['Id'] ?? '').trim();

      if (!displayName) return null;
      return { displayName, email, id };
    })
    .filter((v): v is { displayName: string; email: string; id: string } => Boolean(v));

  // De-dupe by displayName (preserve order)
  const seen = new Set<string>();
  return mapped.filter((x) => {
    const key = x.displayName.toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function extractSuggestionArray(res: unknown): unknown[] {
  if (Array.isArray(res)) return res;
  if (res && typeof res === 'object') {
    const anyRes = res as Record<string, unknown>;
    const value = anyRes['value'];
    if (Array.isArray(value)) return value;
    const results = anyRes['results'];
    if (Array.isArray(results)) return results;
    const data = anyRes['data'];
    if (Array.isArray(data)) return data;
  }
  return [];
}
